/**
 * The filter a stat card applies when it is clicked.
 *
 * A descriptor card names the filter pairs it stands for. That is the whole
 * contract, so routing a click needs nothing about the page it happens on:
 * apply the card's pairs, or clear them when the card is already the applied
 * one. This module holds that routing as plain transforms over a filter model
 * — `{ [param]: value }`, the same vocabulary a card's own map speaks and the
 * same one the grid's `filter[...]` params are built from.
 *
 * A card usually names one pair, but a number describing an intersection
 * ("alarming digitals") names several and only means anything with all of them
 * applied — which is why a card's binding is a map and every operation here
 * treats its pairs as one unit.
 *
 * A model value is either a single token or a list of them: a card over one
 * class of thing names every token in that class, and a multi-select chip over
 * the same param holds its own selection the same way, so a card and a chip can
 * drive one param without either having to know about the other.
 *
 * Contains:
 * - hasStatCardFilterBinding — does a card (or any in a row) bind a filter
 * - statCardFilterParams   — the params the row's cards own between them
 * - isStatCardFilterActive — is this card's filter the one currently applied
 * - toggleStatCardFilter   — apply a card's filter, or clear it when active
 * - setStatCardFilter      — write a param's value directly (the chip path)
 * - statCardFilterValues   — a param's value as a list, for multi-select chips
 * - toFilterParams         — the model as wire-ready filter params
 *
 * Returns plain JS — no JSX, no redux.
 */

/** The pairs a card binds, as entries. A card binding nothing yields none. */
function filterEntries(filters) {
  if (!filters || typeof filters !== 'object') return [];
  return Object.entries(filters);
}

/**
 * Whether any of *cards* binds a filter, which is what makes a card a filter
 * control rather than a readout. Asked of one card, it answers for that card;
 * asked of a row, whether the row holds any filter at all.
 *
 * The distinction is per card, not per row: a Groups or Sites total that binds
 * nothing is a number, and offering it a click would hand the reader a button
 * that changes nothing while drawing itself pressed.
 *
 * @param {Array<{filters: (Object<string, string>|undefined)}>} cards - normalized cards
 * @returns {boolean}
 */
export function hasStatCardFilterBinding(cards) {
  if (!Array.isArray(cards)) return false;
  return cards.some(card => filterEntries(card?.filters).length > 0);
}

/**
 * The filter params a row of cards owns between them — the set a card click is
 * entitled to clear. A param outside it belongs to something else on the page
 * (a filter chip, a scope selector) and survives a click untouched.
 *
 * @param {Array<{filters: (Object<string, string>|undefined)}>} cards - normalized cards
 * @returns {Set<string>}
 */
export function statCardFilterParams(cards) {
  if (!Array.isArray(cards)) return new Set();
  return new Set(cards.flatMap(card => filterEntries(card?.filters).map(([param]) => param)));
}

/** An applied entry is one holding a token or a non-empty list of them. */
function isApplied(value) {
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && value !== '';
}

/**
 * A filter value as the set of tokens it selects. A card naming a class of
 * load names every id in it, a chip over the same param holds the same tokens
 * as a list, and a single token is the one-token set — so the three shapes
 * compare as what they select rather than as how they were written.
 *
 * @param {*} value
 * @returns {Set<string>}
 */
function tokenSet(value) {
  if (!isApplied(value)) return new Set();
  const tokens = Array.isArray(value) ? value : [value];
  return new Set(tokens.map(String));
}

/** Whether two filter values select exactly the same tokens, in any order. */
function selectsSame(current, value) {
  const held = tokenSet(current);
  const wanted = tokenSet(value);
  if (held.size !== wanted.size) return false;
  for (const token of wanted) {
    if (!held.has(token)) return false;
  }
  return true;
}

/**
 * Whether the card binding *filters* is the filter the model currently
 * applies: over the params the card row owns, the model holds exactly what
 * this card names and nothing more.
 *
 * Exactly, not merely including it. Several cards in a row bind the same
 * param — "alarming" alone, and "alarming digital screens" — and reading a
 * card active on a subset draws the broader card pressed while the narrower
 * card's filter is the one applied. So a param the card does not name has to
 * be *unapplied* for the card to be the applied one, which is only a question
 * about params the row owns: a page's own chip over something else narrows
 * the list without stopping the card describing what it describes.
 *
 * *ownedParams* defaults to the card's own params, which is the same question
 * for a row whose cards all bind the same ones.
 *
 * A card binding nothing has no filter of its own for the model to be
 * applying: it is a readout and never reads active.
 *
 * @param {Object<string, *>} model - `{ [param]: value }`.
 * @param {Object<string, (string|string[])>} [filters] - the card's pairs.
 * @param {Iterable<string>} [ownedParams] - the params this row's cards own.
 * @returns {boolean}
 */
export function isStatCardFilterActive(model, filters, ownedParams) {
  const entries = filterEntries(filters);
  if (entries.length === 0) return false;
  const current = model ?? {};
  const named = new Map(entries);
  const scope = new Set([...named.keys(), ...(ownedParams ?? [])]);
  for (const param of scope) {
    if (!selectsSame(current[param], named.get(param))) return false;
  }
  return true;
}

/**
 * Return the model a click on this card produces: every pair the card names
 * applied, or all of them cleared when the card was already the applied one.
 * The pairs move together — half of an intersection is a filter no card is
 * showing a number for. A card binding nothing is a readout and produces no
 * change at all.
 *
 * A card *sets* the card filter rather than adding to it. Clicking one drops
 * every param in *ownedParams* first, so the row reads as one choice: the card
 * showing 40 alarming sites shows 40 rows, not the 3 that are also digital.
 * Params outside that set are somebody else's control and are left alone.
 *
 * @param {Object<string, *>} model
 * @param {Object<string, string>} [filters] - the card's pairs.
 * @param {Iterable<string>} [ownedParams] - the params this row's cards own
 *   (statCardFilterParams). Omitted, only the card's own params are replaced.
 * @returns {Object<string, *>} A new model; *model* is never mutated.
 */
export function toggleStatCardFilter(model, filters, ownedParams) {
  const current = model ?? {};
  const entries = filterEntries(filters);
  if (entries.length === 0) return current;
  const own = new Set(entries.map(([param]) => param));
  if (isStatCardFilterActive(current, filters, ownedParams)) {
    return Object.fromEntries(Object.entries(current).filter(([param]) => !own.has(param)));
  }
  const cleared = new Set([...own, ...(ownedParams ?? [])]);
  const kept = Object.entries(current).filter(([param]) => !cleared.has(param));
  return { ...Object.fromEntries(kept), ...Object.fromEntries(entries) };
}

/**
 * Return the model with *param* set to *value*. An empty value — null,
 * undefined, an empty string, or an empty list — drops the param rather than
 * storing a blank one, so an unapplied filter is absent rather than present
 * and falsy.
 *
 * @param {Object<string, *>} model
 * @param {string} param
 * @param {*} value
 * @returns {Object<string, *>} A new model; *model* is never mutated.
 */
export function setStatCardFilter(model, param, value) {
  const current = model ?? {};
  if (!isApplied(value)) {
    if (!(param in current)) return current;
    const { [param]: _dropped, ...rest } = current;
    return rest;
  }
  return { ...current, [param]: value };
}

/**
 * A param's value as a list, for a multi-select chip bound to the same param
 * a card writes. A single token reads as a one-item list.
 *
 * @param {Object<string, *>} model
 * @param {string} param
 * @returns {string[]}
 */
export function statCardFilterValues(model, param) {
  const current = (model ?? {})[param];
  if (Array.isArray(current)) return current;
  return isApplied(current) ? [current] : [];
}

/**
 * The model as the raw filter keys a grid list request carries. Lists join on
 * commas — the multi-value form the odl_app filter params accept — and
 * unapplied params are omitted.
 *
 * @param {Object<string, *>} model
 * @returns {Object<string, string>}
 */
export function toFilterParams(model) {
  const params = {};
  for (const [param, value] of Object.entries(model ?? {})) {
    if (!isApplied(value)) continue;
    params[param] = Array.isArray(value) ? value.join(',') : String(value);
  }
  return params;
}
